import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { latestListContainerWidget } from "config/resources/sharedFieldsTyped";
import { genericOverTimeChartDto } from "config/utils";
import {
    gitMaintenanceActionSignature,
    purgeBundleActionSignature,
    relInstallBundleActionSignature,
    releaseBundleActionSignature,
    uninstallBundleActionSignature,
    updateBundleActionSignature,
} from "containers/BundleManager/Actions";
import { executionFilterPerBundleLink } from "containers/ExecutionFilter/utils";
import { executionsListSubscription } from "containers/Executions/Executions.type";
import {
    DeleteListAction,
    PermanentlyDeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    exportFullActionSignature,
    exportFullBundleActionSignature,
} from "containers/Resources/components/CommonActions/CommonActions";
import { bulkCommitActionSignature } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import {
    branchNameColumn,
    bundleRepositoryColumn,
    bundleVersionColumn,
    gitEnabledColumn,
    gitUrlColumn,
    repositoryNameColumn,
    resourcesNumberColumn,
} from "containers/Search/components/SearchTabs/columns";
import { executionStatusPerTimeQuery } from "containers/Stats/Stats.type";
import resource from "./resource";

const resourceName = "bundle";

const config: ResourceConfig = {
    label: "Bundle",
    icon: IconCollection.folderTableOutline,
    resourceName: "bundle",
    resourceClass: "resource",
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editIcon: true,
            },
        },
        { partial: "dashboard" },
        { partial: "form" },
        {
            partial: "SearchTabs",
            name: "resourceList",
            filter: (id: string) => ({
                and: [
                    {
                        field: "bundle_id",
                        op: "eq",
                        value: id,
                    },
                ],
            }),
            showLocationColumn: true,
        },
    ],
    actions: {
        shown: [
            bulkCommitActionSignature,
            uninstallBundleActionSignature,
            updateBundleActionSignature,
            relInstallBundleActionSignature,
            releaseBundleActionSignature,
            purgeBundleActionSignature,
            gitMaintenanceActionSignature,
        ],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            exportFullActionSignature,
            exportFullBundleActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
        hidePin: true,
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        gitEnabledColumn(),
        branchNameColumn(setNewValue),
        gitUrlColumn(setNewValue),
        repositoryNameColumn(setNewValue),
        bundleRepositoryColumn(setNewValue),
        bundleVersionColumn(setNewValue),
        resourcesNumberColumn(),
    ],
    graphql: {
        resource: "bundle",
        resourceBody: `
            ${resource.graphql.resourceBody}
            icon
            version
            bundle_repository_id
            bundle_repository_id_bundle_repository{
                id
                name
                has_write_key
            }
            key
            branch_name
            git_url
            is_dirty
            default_track_in_git
            default_is_bundle_content
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            branch_name
            git_url
            repository_name
            resource_list_bundle_id (limit: 0, offset: 0, order: "+name") {
                resource_count
            }
            version
            bundle_repository_id_bundle_repository {
                id
                name
            }
            is_dirty
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)},
            $icon: String,
            $version: String
            $bundle_repository_id: String
            $branch_name: String
        `,
        createQuery: `
        ${resource.graphql.createQuery(resourceName)}
            icon: $icon,
            version: $version,
            bundle_repository_id: $bundle_repository_id,
            branch_name: $branch_name
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)},
            $icon: String,
            $version: String
            $bundle_repository_id: String
            $branch_name: String
            $repository_name: String
            $default_track_in_git: Boolean
            $default_is_bundle_content: Boolean
        `,
        updateQuery: `,
            ${resource.graphql.updateQuery(resourceName)}
            icon: $icon,
            version: $version
            bundle_repository_id: $bundle_repository_id
            branch_name: $branch_name
            repository_name: $repository_name
            default_track_in_git: $default_track_in_git
            default_is_bundle_content: $default_is_bundle_content
        `,
    },
    dashboardQueries: {
        executionStatusPerTime: (recordId) => ({
            query: executionStatusPerTimeQuery,
            variables: {
                interval: 7,
                per: "day",
                bundle_id: recordId,
                allow_normal: true,
                allow_deleted: false,
            },
        }),
    },
    dashboardSubscriptions: {
        latestExecutions: (recordId) => ({
            subscription: executionsListSubscription,
            variables: {
                limit: 3,
                order: "-created_at",
                filter: JSON.stringify({
                    field: "bundle_id",
                    op: "eq",
                    value: recordId,
                }),
            },
        }),
    },
    form: [
        {
            name: "bundle_repository_id",
            component: "autocomplete",
            fieldProps: {
                label: "Bundle repository",
                isReadOnly: false,
                options: {
                    resource: "bundle_repository",
                },
                docs: {
                    text: "The bundle repository from which this bundle was installed from",
                },
            },
        },
        {
            name: "version",
            component: "text",
            fieldProps: {
                label: "Version",
                docs: {
                    text: "Version of the Bundle",
                    url: "/docs/projects#configuration",
                },
            },
        },
    ],
    detailsSidebar: [
        {
            label: "Key",
            valueKey: "key",
            dto: (value: any) =>
                value ? (
                    <>
                        <div>{value}</div>
                    </>
                ) : null,
        },
        {
            label: "Has changes comparing to released/latest version",
            valueKey: "is_dirty",
            dto: (value: any) =>
                value ? (
                    <>
                        <div>Yes</div>
                    </>
                ) : (
                    <>
                        <div>No</div>
                    </>
                ),
        },
    ],
    dashboard: [
        {
            uid: "bundle-dashboard-executions",
            title: "Usage & activity",
            component: "CollapsibleSection",
            className: "w-full",
            defaultExpanded: true,
            items: [
                {
                    uid: "widget-1",
                    title: "Daily executions",
                    className: "w-2/3",
                    component: "OverTimeChart",
                    link: executionFilterPerBundleLink,
                    dataTestId: "dailyExecutions",
                    dto: ({ stats }: any) =>
                        stats?.executionStatusPerTime
                            ? genericOverTimeChartDto(stats?.executionStatusPerTime)
                            : null,
                },
                latestListContainerWidget("widget-2", executionFilterPerBundleLink),
            ],
        },
    ],
};
export default config;
